<script setup lang="ts">
import {
    PredicateValue,
    toPredicateValue,
} from '@precon-tools-suite-by-shared/precon-graphql-types';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import VTooltip from 'primevue/tooltip';
import { predicateValueSymbols } from '../helpers/logic-symbols';

export type PredicateValueDisplayFormat =
    | 'raw'
    | 'answer'
    | 'property'
    | 'modal'
    | 'char'
    | 'description'
    | 'symbol';

const props = withDefaults(
    defineProps<{
        value: string | boolean | PredicateValue | null | undefined;
        defaultValue?: PredicateValue | undefined;
        format?: PredicateValueDisplayFormat;
        noSpan?: boolean;
        color?: boolean;
        tooltip?: PredicateValueDisplayFormat | 'none';
        tooltipDelay?: number;
        is?: string;
    }>(),
    {
        defaultValue: undefined,
        format: 'answer',
        tooltip: 'none',
        tooltipDelay: 500,
        is: 'span',
    }
);

const { t } = useI18n();

const normalizedValue = computed<PredicateValue | undefined>(() =>
    toPredicateValue(props.value, props.defaultValue)
);

const spanClass = computed<string[]>(() => {
    const result: string[] = [`predicate-value-${props.format}`];
    const v = normalizedValue.value;
    if (!v) return result;

    result.push(`predicate-value-${v.toLowerCase()}`);
    if (props.color) result.push('predicate-value-color');

    return result;
});

const tooltipOptions = computed(() => {
    const v = normalizedValue.value;
    if (!v || props.tooltip === 'none') return false;

    return {
        value: getFormattedString(props.tooltip),
        showDelay: props.tooltipDelay,
    };
});

function getFormattedString(format: PredicateValueDisplayFormat) {
    const v = normalizedValue.value;
    if (!v) return undefined;
    switch (format) {
        case 'answer':
        case 'property':
        case 'modal':
        case 'char':
        case 'description':
            return t(`${format}.${v}`);
        case 'symbol':
            return predicateValueSymbols[v];
        case 'raw':
        default:
            return v;
    }
}
</script>

<template>
    <template v-if="noSpan">{{ getFormattedString(props.format) }}</template>
    <template v-else-if="normalizedValue">
        <component
            :is="is"
            v-bind="$attrs"
            v-tooltip.top="tooltipOptions"
            class="predicate-value predicate-value-span"
            :class="spanClass"
            :data-predicate-value="normalizedValue"
            >{{ getFormattedString(props.format) }}</component
        >
    </template>
</template>

<style lang="scss">
.predicate-value-span {
    display: inline-block;
    &.predicate-value-color {
        &.predicate-value-yes {
            color: var(--green-500);
        }
        &.predicate-value-no {
            color: var(--pink-500);
        }
        &.predicate-value-undecidable {
            color: var(--yellow-600);
        }
        &.predicate-value-indeterminate {
            color: var(--bluegray-500);
        }
    }
}
</style>

<i18n>
    {
        "nl": {
            "answer": {
                "YES": "Ja",
                "NO": "Nee",
                "UNDECIDABLE": "Onbeslisbaar",
                "INDETERMINATE": "Onbepaald",
            },
            "property": {
                "YES": "Waar",
                "NO": "Niet Waar",
                "UNDECIDIABLE": "Onbeslisbaar",
                "INDETERMINATE": "Onbekend"
            },
            "modal": {
                "YES": "Altijd",
                "NO": "Nooit",
                "UNDECIDABLE": "Onbeslisbaar",
                "INDETERMINATE": "Onbepaald"
            },
            "char": {
                "YES": "J",
                "NO": "N",
                "UNDECIDABLE": "O",
                "INDETERMINATE": "X"
            },
            "description": {
                "YES": "Is van toepassing.",
                "NO": "Is niet van toepassing.",
                "UNDECIDABLE": "Onmogelijk om automatisch af te leiden. Handmatige beoordeling noodzakelijk.",
                "INDETERMINATE": "Niet genoeg informatie om beoordeling af te leiden."
            }
        },
        "en": {
            "answer": {
                "YES": "Yes",
                "NO": "No",
                "UNDECIDABLE": "Undecidable",
                "INDETERMINATE": "Indeterminate"
            },
            "property": {
                "YES": "True",
                "NO": "False",
                "UNDECIDABLE": "Undecidable",
                "INDETERMINATE": "Unknown"
            },
            "modal": {
                "YES": "Always",
                "NO": "Never",
                "UNDECIDABLE": "Undecidable",
                "INDETERMINATE": "Indeterminate"
            },
            "char": {
                "YES": "Y",
                "NO": "N",
                "UNDECIDABLE": "U",
                "INDETERMINATE": "I"
            },
            "description": {
                "YES": "It applies to this case.",
                "NO": "It does not apply to this case.",
                "UNDECIDABLE": "Inpossible to derive automatically. A manual judgement is required.",
                "INDETERMINATE": "Not enough information to derive judgement."
            }
        },
        "fr": {
            "answer": {
                "YES": "Oui",
                "NO": "Non",
                "UNDECIDABLE": "Indécidable",
                "INDETERMINATE": "Indéfini"
            },
            "property": {
                "YES": "Vraie",
                "NO": "Faux",
                "UNDECIDABLE": "Indécidable",
                "INDETERMINATE": "Inconnu"
            },
            "modal": {
                "YES": "Toujours",
                "NO": "Jamais",
                "UNDECIDABLE": "Indécidable",
                "INDETERMINATE": "Indéfini"
            },
            "char": {
                "YES": "O",
                "NO": "N",
                "UNDECIDABLE": "I",
                "INDETERMINATE": "X"
            }
        }
    }
</i18n>
